import { useContext, createContext } from 'react';
import { dispatchGTMEvent } from './dispatchGTMEvent';
import type { GtmGenericEvent } from './types.events';

type TrackingContext = {
  /** GTM id */
  gtmId: string;
  /** gtm_auth value */
  auth?: string;
  /** gtm_preview value */
  preview?: string;
  /** name of the GTM datalayer page variable
   * @default 'dataLayer'
   */
  dataLayerName?: string;
};

const trackingContext = createContext<TrackingContext>({
  gtmId: '',
  auth: '',
  preview: '',
  // don't set 'dataLayerName' here so the default value in context destructuring works
});

const TrackingProvider = trackingContext.Provider;

const useTracking = (): (<T extends GtmGenericEvent<any>>(event: T) => void) => {
  const { auth, gtmId, preview, dataLayerName = 'dataLayer' } = useContext(trackingContext);
  const gtmAuth = auth ? `&gtm_auth=${auth}` : '';
  const gtmPreview = preview ? `&gtm_preview=${preview}` : '';

  if (typeof window !== 'undefined' && gtmId !== '' && !(window as any)[dataLayerName]) {
    // load GTM if dataLayer doesn't exist yet
    (window as any)[dataLayerName] = [];
    const script = document.createElement('script');
    script.setAttribute('data-testid', 'gtm-script');
    script.innerHTML = `!function(t,e,n,o,a){t[o]=t[o]||[],t[o].push({"gtm.start":(new Date).getTime(),event:"gtm.js"});var d=e.getElementsByTagName(n)[0],m=e.createElement(n);m.async=!0,m.src="https://www.googletagmanager.com/gtm.js?id=${gtmId}&l="+o+"${gtmAuth}${gtmPreview}&gtm_cookies_win=x",m.onload=function(){m.onload=null,window.gmtLoaded=!0,window.gmtBlocked=!1;var t=document.createEvent("Event");t.initEvent("gtmLoaded",!1,!0),e.dispatchEvent(t)},m.onerror=function(){m.onerror=null,window.gmtLoaded=!0,window.gmtBlocked=!0;var t=document.createEvent("Event");t.initEvent("gtmLoaded",!1,!0),e.dispatchEvent(t)},d.parentNode.insertBefore(m,d)}(window,document,"script","${dataLayerName}");`;
    document.head.insertBefore(script, document.head.firstElementChild);
  }
  // if there is no dataLayer when gtm is disabled, set it to empty array
  if (typeof window !== 'undefined' && !(window as any)[dataLayerName]) {
    (window as any)[dataLayerName] = [];
  }

  return <T extends GtmGenericEvent>(event: T) => dispatchGTMEvent<T>(event, dataLayerName);
};

export { TrackingProvider, useTracking };
